/**
 * Minimal DOM helpers.
 *
 * KinCode uses vanilla JS with light-DOM Web Components (§19); there is no
 * framework. These helpers keep imperative construction readable without
 * becoming one. `el` sets properties, attributes and children; `icon` builds a
 * consistently styled, correctly labelled <wa-icon>.
 */

import { ICON_FAMILY } from "../config/icons.js";

/**
 * Create an element.
 *
 * Keys in `props` map to properties when they exist on the element and to
 * attributes otherwise, so both `id`/`className` and Web Awesome attributes like
 * `variant` work. `dataset` and `on` (event listeners) are handled specially.
 *
 * @param {string} tag
 * @param {Record<string, unknown> & {
 *   dataset?: Record<string, string>,
 *   on?: Record<string, EventListener>,
 * }} [props]
 * @param {(Node | string | null | undefined)[]} [children]
 * @returns {HTMLElement}
 */
export function el(tag, props = {}, children = []) {
  const node = document.createElement(tag);
  for (const [key, value] of Object.entries(props)) {
    if (value === undefined || value === null || value === false) {
      continue;
    }
    if (key === "dataset") {
      Object.assign(node.dataset, value);
    } else if (key === "on" && typeof value === "object") {
      for (const [event, listener] of Object.entries(/** @type {object} */ (value))) {
        node.addEventListener(event, /** @type {EventListener} */ (listener));
      }
    } else if (key === "class") {
      node.setAttribute("class", String(value));
    } else if (value === true) {
      node.setAttribute(key, "");
    } else if (key in node) {
      // @ts-expect-error indexing an element by a known-writable key.
      node[key] = value;
    } else {
      node.setAttribute(key, String(value));
    }
  }
  node.append(...children.filter((child) => child !== null && child !== undefined));
  return node;
}

/**
 * Create a labelled or decorative icon.
 *
 * Pass `label` for an icon that conveys meaning; omit it for a purely decorative
 * icon, which is then hidden from assistive technology (§4). Never leave a
 * meaningful icon unlabelled.
 *
 * @param {string} name Font Awesome icon name, e.g. "key".
 * @param {{ label?: string }} [options]
 * @returns {HTMLElement}
 */
export function icon(name, options = {}) {
  /** @type {Record<string, unknown>} */
  const props = { name, family: ICON_FAMILY };
  if (options.label) {
    props.label = options.label;
  } else {
    props["aria-hidden"] = "true";
  }
  return el("wa-icon", props);
}

/**
 * Read the trimmed string value of a form control.
 *
 * Web Awesome inputs expose a `value` property at runtime, but their JSDoc types
 * surface as plain `HTMLElement` here, so reading `.value` needs a cast. Doing it
 * in one helper keeps that cast — and the consistent trimming — out of every
 * page.
 *
 * @param {Element} field
 * @returns {string}
 */
export function fieldValue(field) {
  const value = /** @type {{ value?: unknown }} */ (/** @type {unknown} */ (field)).value;
  return typeof value === "string" ? value.trim() : "";
}

/**
 * Create a fragment from a list of nodes.
 *
 * @param {(Node | string | null | undefined)[]} children
 * @returns {DocumentFragment}
 */
export function fragment(children) {
  const frag = document.createDocumentFragment();
  frag.append(...children.filter((child) => child !== null && child !== undefined));
  return frag;
}
